// components/common/rich-text-viewer/RichTextViewer.tsx
"use client";

import React, { useMemo } from "react";
import { mdLiteToHtml } from "../../../../utils/text/mdLite";
import { isRTLText } from "../../../../utils/helpers/languageHelpers";
import "./RichTextViewer.css";

type Props = {
  value?: string | null;              // md-lite
  className?: string;
  dir?: "rtl" | "ltr";                // optional override
  placeholder?: string;               // empty hint
  appearance?: "light" | "dark";      // new: adapt to bg (default: light)
};

const RichTextViewer: React.FC<Props> = ({
  value,
  className = "",
  dir,
  placeholder,
  appearance = "light",               // default: dark text on light bg
}) => {
  // convert md-lite → HTML (supports bold/italic/etc.)
  const html = useMemo(() => mdLiteToHtml(value || ""), [value]);

  // auto RTL/LTR unless overridden
  const resolvedDir: "rtl" | "ltr" = useMemo(() => {
    if (dir) return dir;
    return isRTLText(value || "") ? "rtl" : "ltr";
  }, [dir, value]);

  const themeClass = appearance === "dark" ? "rtv--dark" : "rtv--light";

  if (!value || value.trim() === "") {
    return (
      <div className={`rtv-root ${themeClass} ${className}`} dir={resolvedDir}>
        {placeholder ? <span className="rtv-placeholder">{placeholder}</span> : null}
      </div>
    );
  }

  return (
    <div
      className={`rtv-root ${themeClass} ${className}`}
      dir={resolvedDir}
      // ok: trusted md-lite → sanitized HTML output of your converter
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default RichTextViewer;
